import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  issueCustodianCertifiedCopy,
  listCustodianCopyRequests,
} from '../../api/custodianApi';
import { CustodianFilePreview } from '../../components/custodian/CustodianFilePreview';
import { ErrorBanner } from '../../components/ui/ErrorBanner';

const NOT_ADMITTED = 'Package not admitted';

function displayCell(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function ReleaseCopyQueuePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [busyId, setBusyId] = useState('');
  const [issuedCopy, setIssuedCopy] = useState(null);
  const [previewPackageId, setPreviewPackageId] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listCustodianCopyRequests({ page: 1, pageSize: 50 });
        if (cancelled) return;
        setRows(response?.items || []);
      } catch (requestError) {
        if (!cancelled) {
          setRows([]);
          setError(requestError.message || 'Unable to load copy requests.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  async function issue(requestId) {
    setBusyId(requestId);
    setError('');
    try {
      const response = await issueCustodianCertifiedCopy(requestId);
      setIssuedCopy(response);
      setReloadToken((value) => value + 1);
    } catch (requestError) {
      setError(requestError.message || 'Unable to issue certified copy.');
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="page-stack">
      <section className="card-shell p-5 sm:p-6">
        <p className="text-sm leading-relaxed text-evault-muted">
          Certified copies are issued from an admitted vault record. Authoritative
          means the sealed vault record. The watermarked file is a copy, and a
          certified true copy is still a copy.
        </p>
      </section>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      {issuedCopy ? (
        <section className="card-shell p-4" role="status">
          <p className="text-sm text-umb-neutral-dark">
            You are viewing: {issuedCopy.displayed_copy}
          </p>
          <p className="mt-1 break-all font-mono text-xs text-umb-neutral-dark">
            Rendition SHA-256: {issuedCopy.rendition_sha256}
          </p>
          <p className="mt-2 text-xs text-evault-muted">
            This issued file is a watermarked copy. It is not the sealed vault record.
          </p>
        </section>
      ) : null}

      <div className="table-shell overflow-hidden">
        <div className="border-b border-evault-border bg-evault-surface/80 px-4 py-3">
          <h2 className="text-sm font-semibold text-umb-neutral-dark">Copy requests</h2>
        </div>
        {error ? null : loading ? (
          <p className="px-4 py-6 text-sm text-evault-muted">Loading copy requests…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-evault-muted">No copy requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] border-collapse text-left text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-evault-border bg-evault-surface/90">
                  <th className="px-4 py-3 font-semibold text-evault-muted">Asset</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Status</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Copy state</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Notes</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Blocker</th>
                  <th className="px-4 py-3 font-semibold text-evault-muted">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const alreadyIssued =
                    row.copy_state === 'ISSUED' || row.status === 'COMPLETED';
                  const issueDisabled =
                    row.blocker === NOT_ADMITTED || busyId === row.id || alreadyIssued;
                  return (
                    <tr key={row.id} className="border-b border-evault-border last:border-b-0">
                      <td className="px-4 py-3.5">
                        <Link
                          to={`/custodian/assets/${encodeURIComponent(row.package_id)}`}
                          className="font-mono text-[11px] hover:underline sm:text-xs"
                        >
                          {displayCell(row.asset_number)}
                        </Link>
                      </td>
                      <td className="px-4 py-3.5">{displayCell(row.status)}</td>
                      <td className="px-4 py-3.5">{displayCell(row.copy_state)}</td>
                      <td className="max-w-[16rem] px-4 py-3.5">{displayCell(row.notes)}</td>
                      <td className="px-4 py-3.5">{displayCell(row.blocker)}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full border border-evault-border bg-white px-3 py-1.5 text-xs font-semibold text-evault-navy"
                            onClick={() => setPreviewPackageId(row.package_id)}
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full bg-evault-teal-deep px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={issueDisabled}
                            onClick={() => {
                              if (issueDisabled) return;
                              issue(row.id);
                            }}
                          >
                            {busyId === row.id ? 'Issuing…' : alreadyIssued ? 'Issued' : 'Issue'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewPackageId ? (
        <section className="card-shell p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-umb-neutral-dark">Labelled copy preview</h2>
            <button
              type="button"
              className="text-xs font-semibold text-evault-muted hover:text-umb-neutral-dark"
              onClick={() => setPreviewPackageId('')}
            >
              Close preview
            </button>
          </div>
          <CustodianFilePreview key={previewPackageId} packageId={previewPackageId} />
        </section>
      ) : null}
    </div>
  );
}
